import React, { createContext, ReactElement, ReactNode, useCallback, useContext } from 'react'

import { EmberEventFn } from '../../../types/emberEventFn'

interface CreateNewContactOptions {
  isSupplier?: boolean
  isCustomer?: boolean
}

interface ContextState {
  createNewContact: ({ isSupplier, isCustomer }: CreateNewContactOptions) => void
  editContact: (contactId: string) => void
}

const ContactsEmberConnectionContext = createContext<ContextState | undefined>(undefined)

interface ContactsEmberConnectionContextProps {
  children?: ReactNode
  onCreateNewContact: EmberEventFn
  onEditContact: EmberEventFn
}

export const ContactsEmberConnectionContextProvider = ({
  onCreateNewContact,
  onEditContact,
  children,
}: ContactsEmberConnectionContextProps): ReactElement => {
  const createNewContact = useCallback(
    ({ isSupplier = false, isCustomer = true }) => {
      onCreateNewContact({ detail: { isSupplier, isCustomer } })
    },
    [onCreateNewContact],
  )

  const editContact = useCallback(
    (contactId: string) => {
      onEditContact({ detail: { contactId } })
    },
    [onEditContact],
  )

  return (
    <ContactsEmberConnectionContext.Provider value={{ createNewContact, editContact }}>
      {children}
    </ContactsEmberConnectionContext.Provider>
  )
}

export const useContactsEmberConnection = () => {
  const context = useContext(ContactsEmberConnectionContext)

  if (!context) {
    throw new Error('ContactsEmberConnectionContext is missing in the module!')
  }

  return context
}
